class NestedMegaMenuNav extends HTMLElement {
  connectedCallback() {
    this.level2Items = this.querySelectorAll('.mega-menu__link--level-2')
    this.level3Items = this.querySelectorAll('.mega-menu__link--level-3')
    this.level3Panels = this.querySelectorAll('.mega-menu__level-3-wrapper')
    this.subItemBlocks = this.querySelectorAll('.mega-menu__sub-item-blocks')
    this.defaultBlocks = this.querySelectorAll('.mega-menu__default-blocks')

    this.addEventListener('mouseover', this.onMouseOver.bind(this))

    // Add hover listeners for nav items to activate on mega menu hover
    this.querySelectorAll('.header__menu-block').forEach(menuBlock => {
      const navItem = menuBlock.querySelector('.header__menu-item')
      const megaMenu = menuBlock.querySelector('.mega-menu')

      if (navItem && megaMenu) {
        menuBlock.addEventListener('mouseenter', () => {
          // Activate first level-2 item when opening the mega menu
          const firstItem = megaMenu.querySelector('.mega-menu__link--level-2')
          if (firstItem?.dataset.key) {
            this.setActive(firstItem.dataset.key, navItem)
          } else {
            navItem.classList.add('active')
            this.hideAllBlocks()
            this.showDefaultBlocks()
          }
        })

        menuBlock.addEventListener('mouseleave', () => {
          navItem.classList.remove('active')

          // Clear active states
          this.level2Items.forEach(item => item.classList.remove('active'))
          this.level3Items.forEach(item => item.classList.remove('active'))

          this.hideAllBlocks()
        })
      }
    })
  }

  onMouseOver(event) {
    // Prevent rapid firing and conflicts
    if (this._processingHover) return
    this._processingHover = true

    // Clear any pending timeout
    if (this._hoverTimeout) {
      clearTimeout(this._hoverTimeout)
    }

    this._hoverTimeout = setTimeout(() => {
      // First priority: grandchild (level-3)
      const level3Item = event.target.closest('.mega-menu__link--level-3')
      if (level3Item && this.contains(level3Item)) {

        const navItem = level3Item
          .closest('.header__menu-block')
          ?.querySelector('.header__menu-item')

        const key = level3Item.dataset.key
        if (key && navItem) {
          this.setActive(key, navItem)
        }
        this._processingHover = false
        return
      }

      // Second: child (level-2)
      const level2Item = event.target.closest('.mega-menu__link--level-2')
      if (!level2Item || !this.contains(level2Item)) {
        this._processingHover = false
        return
      }

      const navItem = level2Item
        .closest('.header__menu-block')
        ?.querySelector('.header__menu-item')

      const key = level2Item.dataset.key
      if (key && navItem) {
        this.setActive(key, navItem)
      }

      this._processingHover = false
    }, 5) // Very small delay to debounce
  }

  /**
   * key can be either:
   *  - "parent--child" (level-2)
   *  - "parent--child--grandchild" (level-3)
   */
  setActive(key, navItem) {

    if (navItem) {
      navItem.classList.add('active')
    }

    const level2Key = this.getLevel2KeyFromKey(key)

    // Get the current mega menu container to scope all operations
    const currentMegaMenu = navItem?.closest('.header__menu-block')?.querySelector('.mega-menu')

    if (!currentMegaMenu) {
      return;
    }


    // Clear ALL level-2 and level-3 items first (only within current mega menu)
    currentMegaMenu.querySelectorAll('.mega-menu__link--level-2, .mega-menu__link--level-3').forEach((item) => {
      item.classList.remove('active')
      item.setAttribute('aria-selected', 'false')
    })

    // Update level-2 items (only exact match within current mega menu)
    currentMegaMenu.querySelectorAll('.mega-menu__link--level-2').forEach((item) => {
      const itemKey = item.dataset.key
      const isActive = itemKey && level2Key && itemKey === level2Key
      item.classList.toggle('active', !!isActive)
      item.setAttribute('aria-selected', !!isActive)
    })

    // Update level-3 items (only exact match within current mega menu)
    currentMegaMenu.querySelectorAll('.mega-menu__link--level-3').forEach((item) => {
      const isActive = item.dataset.key === key
      item.classList.toggle('active', isActive)
      item.setAttribute('aria-selected', isActive)
    })

    // Show the correct level-3 panel based on the level-2 portion of the key
    currentMegaMenu.querySelectorAll('.mega-menu__level-3-wrapper').forEach((panel) => {
      panel.hidden = panel.dataset.key !== level2Key
    })

    // Blocks: show grandchild block if exists and has visible content, otherwise fallback to default
    const hasMatchingSubBlocks = Array.from(this.subItemBlocks).some(
      block => block.dataset.key === key && !block.querySelector('[data-no-image]')
    )

    this.hideAllBlocks()

    if (hasMatchingSubBlocks) {
      this.showSubItemBlocks(key)
    } else {
      this.showDefaultBlocks()
    }
  }

  getLevel2KeyFromKey(key) {
    if (!key) return key
    const parts = key.split('--')
    if (parts.length >= 2) {
      return `${parts[0]}--${parts[1]}`
    }
    return key
  }

  showSubItemBlocks(key) {
    // Show only matching grandchild block(s)
    this.subItemBlocks.forEach((blockContainer) => {
      const isMatch = blockContainer.dataset.key === key
      blockContainer.style.display = isMatch ? 'flex' : 'none'

      // Hide parent .mega-menu-blocks with opacity if no visible content (to prevent layout shift)
      if (isMatch) {
        const megaMenuBlocks = blockContainer.closest('.mega-menu-blocks')
        if (megaMenuBlocks) {
          const hasContent = blockContainer.querySelector('.mega-menu-block__container')
          megaMenuBlocks.style.opacity = hasContent ? '' : '0'
        }
      }
    })
  }

  showDefaultBlocks() {
    // Show all default blocks (each mega menu has its own)
    this.defaultBlocks.forEach((defaultBlock) => {
      defaultBlock.style.display = 'block'
      // Reset parent .mega-menu-blocks visibility
      const megaMenuBlocks = defaultBlock.closest('.mega-menu-blocks')
      if (megaMenuBlocks) {
        megaMenuBlocks.style.opacity = ''
      }
    })
  }

  hideAllBlocks() {
    this.subItemBlocks.forEach((block) => {
      block.style.display = 'none'
    })
    this.defaultBlocks.forEach((block) => {
      block.style.display = 'none'
    })
  }
}

customElements.define('nested-mega-menu-nav', NestedMegaMenuNav)
